/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  setFilterDialogRoute,
} from '@gh/builder-shared';
import { AelEvents } from '@gh/builder-shared-data';
import { createDefaultGridModel, ROW_DIRTY_MARKER_CLASS } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  DateTimeTypes,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
  isDirty,
  NumberTypes,
} from '@gh/common-shared';
import { DateFormat } from '@gh/core-data';
import { isMessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  cancelAnswer,
  Confirmation,
  DialogService,
  noAnswer,
  NotificationService,
  RendererDirective,
  WaitingService,
  yesAnswer,
  yesNoCancelConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildEditableAutocompleteColumn,
  buildEditableDateTimeColumn,
  buildHeaderWithExternalFilter,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { currentDate, notNil, toBoolean } from '@gh/core-util';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { CellEditingStartedEvent, ColDef, ColGroupDef } from 'ag-grid-community';
import { compact, isEmpty, isNil, range } from 'lodash';
import { PreConstructionWorksheetBusinessService } from '../../resources/pre-construction-worksheet-business.service';
import {
  buildPreConstructionWorksheetTaskTree,
  equalizePreConstructionWorksheetTaskTree,
  generatePreConstructionWorksheetTaskFieldName,
  getPreConstructionScheduleComplexField,
  getPreConstructionWorksheetScheduleId,
  getPreConstructionWorksheetTaskTreeMaxDepth,
  PRE_CONSTRUCTION_WORKSHEET_MAX_EDITED_SCHEDULES,
  PreConstructionWorksheetDetails,
  preConstructionWorksheetDetailsToSaveDto,
  PreConstructionWorksheetSchedule,
  PreConstructionWorksheetTaskTreeNode,
} from '../../resources/pre-construction-worksheet.model';
import { PreConstructionWorksheetScheduleFormState } from './pre-construction-worksheet-form.service';
import { worksheetTemplateDialogRoute } from './worksheet-template-dialog/worksheet-template-dialog.component';
import { Observable } from 'rxjs';

@Component({
  selector: 'gh-pre-construction-worksheet',
  template: require('./pre-construction-worksheet.component.html'),
  styles: [require('./pre-construction-worksheet.component.scss')],
  providers: [PreConstructionWorksheetScheduleFormState],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreConstructionWorksheetComponent extends AbstractFormComponent<PreConstructionWorksheetDetails> implements OnInit {
  definition: GridDefinition;

  @ViewChild(RendererDirective, { static: false }) renderer: RendererDirective;
  get isProcessEnabled(): boolean {
    return  this.form.dirty && !!preConstructionWorksheetDetailsToSaveDto(this.form.value).schedules.length;
  }

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private router: AppRouter,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private navigations: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              public form: PreConstructionWorksheetScheduleFormState,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private business: PreConstructionWorksheetBusinessService) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<PreConstructionWorksheetDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute(({ value }) => this.business.save(value)),
      finish: ({ value }) => {
        this.form.build(value);
      },
      error: ({ error }) => {
        if (isMessageBasket(error)) {
          if (error.has('msg_nominated_template_not_set')) {
            this.selectTemplateAndRefresh();
          } else if (error.has('msg_worksheet_template_changed')) {
            this.loadTemplateAndRefresh();
            this.notifications.processAll(error);
          } else {
            this.notifications.processAll(error);
          }
        }
      },
    });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);

    this.init(details);
  }

  undo(): void {
    this.form.undo();
  }

  redo(): void {
    this.form.redo();
  }

  onShowWithSlabDateOnlyChange(showWithSlabDateOnly: boolean): void {
    this.form.setFilter('showWithSlabDateOnly', showWithSlabDateOnly);
  }

  onCellEditingStarted(event: CellEditingStartedEvent): void {
    const schedule: PreConstructionWorksheetSchedule = event.data;

    if (!isDirty(schedule) && this.form.countOfEditedSchedules >= PRE_CONSTRUCTION_WORKSHEET_MAX_EDITED_SCHEDULES) {
      const confirmation$ = this.dialogs.confirmation({
        messageId: 'msg_worksheet_save_first',
        buttons: [Confirmation.Yes, Confirmation.No, Confirmation.Cancel],
        yesLabel: this.translations.get('lbl_save'),
        noLabel: this.translations.get('lbl_discard'),
        cancelLabel: this.translations.get('lbl_continue'),
        defaultButton: Confirmation.Cancel,
      });

      confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.completeFlow.run({
          trigger: 'process',
          form: this.form,
          value: this.form.submitValue,
        }).toFinalState()),
        filter$(({ success }) => Boolean(success)))
        .subscribe();

      confirmation$.pipe(filter$(noAnswer))
        .subscribe(() => this.form.revert());

      confirmation$.pipe(filter$(cancelAnswer))
        .subscribe(() => {
          this.form.restrictEditingToDirtySchedules();
          notNil(event.api).stopEditing();
        });
    }
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityType: 'JOB',
      eventTypeIds: [AelEvents.Job.PreConstructionSchedule.Edited],
      label: 'lbl_pre_construction_worksheet',
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.isProcessEnabled) {
      return true;
    }
    const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));
    confirmation$.pipe(filter$(yesAnswer),
                       switchMap$(() => this.business.save(this.form.value).pipe(
                         this.waitings.operator()))).subscribe(() => {}, this.notifications.processAll);
    confirmation$.pipe(filter$(noAnswer)).subscribe(() => true);
    return confirmation$.pipe(map$((res) => !!res));
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      editable: this.form.editable$,
      getId: getPreConstructionWorksheetScheduleId,
      getCellDef: this.form.getCellDef.bind(this.form),
      getFieldValue: getPreConstructionScheduleComplexField,
      validate: this.form.validateField.bind(this.form),
      command: (entity, name, value) => this.form.setScheduleField(entity, name, value),
    });
    // calculator of the next color class
    let lastColorClassIndex = -1;
    const nextColumnColorClass = (): string => {
      lastColorClassIndex = (lastColorClassIndex + 1) % 13; // tslint:disable-line:no-magic-numbers
      return `m-task-color-${lastColorClassIndex + 1}`;
    };
    // factory of column or column group
    // tslint:disable-next-line:max-line-length
    const createWorksheetColDef = ({ task: templateTask, children, last, virtual, parent }: PreConstructionWorksheetTaskTreeNode,
                                   colorClass: string): ColDef | ColGroupDef => {
      if (isEmpty(children)) {
        const field = generatePreConstructionWorksheetTaskFieldName(templateTask.templateTaskId);
        return {
          field,
          sortable: false,
          headerName: virtual ? '' : templateTask.name,
          headerClass: compact([
            last ? 'grid-full-height-resize-handle' : void 0,
            colorClass,
          ]),
          cellClass: compact([last ? 'cell--right-border' : void 0, colorClass]),
          cellClassRules: {
            'editable-cell--invalid': ({ data }: { data: PreConstructionWorksheetSchedule }) => {
              const task = data.scheduleTasks[templateTask.templateTaskId];
              return task && isNil(task.completionDate) && task.endDate < currentDate();
            },
          },
          ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor(field), {
            format: DateFormat.Date,
            clearable: false,
          }),
        };
      } else {
        return {
          headerName: virtual ? '' : templateTask.name,
          headerClass: compact([
            'grid-has-resize-handle',
            last ? 'grid-full-height-resize-handle' : void 0,
            colorClass,
            parent ? 'text-uppercase' : void 0,
          ]),
          cellClass: compact([last ? 'cell--right-border' : void 0, colorClass]),
          children: children.map((child) => createWorksheetColDef(child, colorClass)),
        };
      }
    };

    // create empty headers between leaf headers and top header
    const levelColumns = (columnDefs: ColDef[], depth: number): ColDef[] =>
      range(depth - 2) // tslint:disable-line:no-magic-numbers
        .reduce(
          (children) => [({
            headerName: '',
            headerClass: 'grid-full-height-resize-handle',
            children,
          })],
          columnDefs);

    const taskTree = buildPreConstructionWorksheetTaskTree(this.form.templateTasks);
    // minimal depth = 2, this is necessary to make grid headers (according to the UI design)
    // tslint:disable-next-line:no-magic-numbers
    const maxDepth: number = Math.max(getPreConstructionWorksheetTaskTreeMaxDepth(taskTree), 2);
    equalizePreConstructionWorksheetTaskTree(taskTree, maxDepth);

    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      options: {
        rowClassRules: {
          [ROW_DIRTY_MARKER_CLASS]: ({ data: { isDirty: isDirtyFlag } }: { data: PreConstructionWorksheetSchedule }) =>
            toBoolean(isDirtyFlag),
          'm-row-dpp': ({ data }: { data: PreConstructionWorksheetSchedule }) => data.isDpp,
          // tslint:disable-next-line:max-line-length
          'm-row-auth-to-commence': ({ data }: { data: PreConstructionWorksheetSchedule }) => data.isAuthToCommenceDateSet,
        },
      },
      columns: [{
        headerName: this.translations.get('lbl_worksheet'),
        headerClass: ['header-group-title', 'grid-full-height-resize-handle'],
        children: levelColumns(
          [{
            field: 'jobNumber',
            headerName: this.translations.get('lbl_job_number'),
            width: 100,
            comparator: (fieldA, fieldB) => {
              const rivenA = fieldA.split('-');
              const rivenB = fieldB.split('-');
              const valA = rivenA[1];
              const valB = rivenB[1];
              const prefixA = rivenA[0];
              const prefixB = rivenB[0];
              if (prefixA > prefixB) {
                return 1;
              }
              if (prefixA < prefixB) {
                return -1;
              }
              if (valA > valB) {
                return 1;
              }
              if (valA < valB) {
                return -1;
              }
              return 0;
            },
            pinned: 'left',
          }, {
            field: 'customerName',
            headerName: this.translations.get('lbl_owner'),
            pinned: 'left',
            tooltipField: 'customerName',
          }, {
            field: 'siteAddress',
            headerName: this.translations.get('lbl_site_address'),
            pinned: 'left',
            width: 200,
            tooltipField: 'siteAddress',
          }, {
            field: 'stageName',
            headerName: this.translations.get('lbl_job_stage'),
            pinned: 'left',
            width: 120,
            tooltipField: 'stageName',
          }],
          maxDepth),
      }, {
        headerName: this.translations.get('lbl_job_accepted'),
        headerClass: ['header-group-sub-title', 'grid-full-height-resize-handle'],
        children: levelColumns(
          [{
            field: 'salespersonName',
            headerName: this.translations.get('lbl_salesperson'),
            ...buildHeaderWithExternalFilter({
              title: this.translations.get('lbl_filter_salespersons'),
              filtered: this.form.hasSalespersonFilter$,
              onFilter: () => this.openSalespersonFilter(),
            }),
          }, {
            field: 'rfqProducedDate',
            headerName: this.translations.get('lbl_rfq_printed'),
            ...buildDateTimeColumn(DateTimeTypes.ShortDate),
          }, {
            field: 'floorArea',
            headerName: this.translations.get(IS_REGION_AU_OR_NZ ? 'lbl_floor_area_m2' : 'lbl_floor_area_ft2'),
            ...buildNumberColumn(NumberTypes.Area),
          }, {
            field: 'contractAmount',
            headerName: this.translations.get('lbl_contract_value_excl_gst'),
          }, {
            field: 'estimatorName',
            headerName: this.translations.get('lbl_estimator'),
          }, {
            field: 'jobAdmin',
            headerName: this.translations.get('lbl_administrator'),
            ...buildHeaderWithExternalFilter({
              title: this.translations.get('lbl_filter_admins'),
              filtered: this.form.hasJobAdminFilter$,
              onFilter: () => this.openJobAdminFilter(),
            }),
            ...buildEditableAutocompleteColumn(
              {
                nameProperty: ({ userName }) => userName,
                loadMore: (event) => event.load(this.business.findAllJobAdministrators(event.query)),
              },
              model.getCellAccessor('jobAdmin')),
          }, {
            field: 'rfqAuthoritySignedDate',
            headerName: this.translations.get('lbl_authority_to_proceed_sign'),
            headerClass: 'grid-full-height-resize-handle',
            cellClass: 'cell--right-border',
            ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('rfqAuthoritySignedDate'), {
              format: DateFormat.Date,
              clearable: false,
            }),
          }],
          maxDepth),
      },
        ...taskTree.map((root) => createWorksheetColDef(root, nextColumnColorClass())),
      ],
    });
  }

  private openSalespersonFilter(): void {
    this.dialogs.openWithInput(setFilterDialogRoute, {
      title: this.translations.get('lbl_select_salespersons'),
      groupTitle: this.translations.get('lbl_salespersons'),
      items: this.form.salespersons,
      selected: this.form.selectedSalespersonIds,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((ids) => this.form.setFilter('salespersonIds', ids));
  }

  private openJobAdminFilter(): void {
    this.dialogs.openWithInput(setFilterDialogRoute, {
      title: this.translations.get('lbl_select_administrators'),
      groupTitle: this.translations.get('lbl_administrators'),
      items: this.form.jobAdmins,
      selected: this.form.selectedJobAdminIds,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((ids) => this.form.setFilter('jobAdminIds', ids));
  }

  private selectTemplateAndRefresh(): void {
    this.dialogs.openWithInput(worksheetTemplateDialogRoute, {})
      .afterClosed().pipe(
      filter$(Boolean),
      switchMap$((templateId: any) => this.business.init(templateId).pipe(this.waitings.operator())))
      .subscribe(
        (details: any) => this.refresh(details),
        this.notifications.processAll);
  }

  private loadTemplateAndRefresh(): void {
    this.business.getNominatedTemplate().pipe(
      switchMap$((templateId) => this.business.init(templateId)),
      this.waitings.operator())
      .subscribe(
        (details) => this.refresh(details),
        this.notifications.processAll);
  }

  private init(details: PreConstructionWorksheetDetails): void {
    this.form.build(details);

    this.definition = this.buildGridDefinition();
  }

  private refresh(details: PreConstructionWorksheetDetails): void {
    this.init(details);

    this.renderer.refresh();
  }
}
